<template>
  <div class="group-list">
    <h1 class="common__main-title">Clock-in Details</h1>
    <div class="name-date-box">
      <div v-if="tutor" class="tutor-name">
        <span class="tutor-info">{{ tutor.id }}</span>
        {{ tutor.name }}
      </div>
      <Datepicker
        v-if="hasAuth(['admin', 'senior'])"
        :input-class="'datepickerInput'"
        :wrapper-class="'lesson-date'"
        :value="date.toDate()"
        @input="changeDateOnCalendar"
        :format="'yyyy-MM-dd'"
        name="datepicker"
      />
    </div>
    <div class="d-flex justify-content-end mb-3">
      <b-button
        v-if="hasAuth(['admin', 'senior'])"
        pill
        variant="primary"
        @click="addTime"
      >
        Add
      </b-button>
    </div>
    <StampHistories
      :stamps="histories"
      @deleteStamp="confirmDelete"
      @editHistory="editTime"
    />
    <div class="d-flex">
      <b-button
        pill
        variant="secondary"
        :to="{
          name:'ClockIns',
          params: { date: date.format('YYYYMMDD') },
        }"
      >
        Back
      </b-button>
    </div>
    <ConfirmModal ref="confirmModal" isLoadingMode @ok="deleteStamp" />
    <SuccessModal ref="sucessModal" @ok="$router.go(0)"/>
    <AddModal ref="addModal" isLoadingMode @ok="registerStamp" />
    <EditModal
      ref="editModal"
      isLoadingMode
      @ok="changeStamp"
    />
    <ErrorModal ref="errorModal" />
    <Loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import StampHistories from '@/components/parts/clockIn/StampHistories.vue'
import ConfirmModal from '@/components/parts/modal/Confirm.vue'
import SuccessModal from '@/components/parts/modal/Success.vue'
import AddModal from '@/components/parts/modal/AddTime.vue'
import EditModal from '@/components/parts/modal/EditTime.vue'
import ErrorModal from '@/components/parts/modal/Error.vue'
import Datepicker from 'vuejs-datepicker'
import moment, { Moment } from 'moment'
import _ from 'lodash'
import Loading from '@/components/parts/modal/Loading.vue'
import { clockInType, Stamp } from '@/store/clockIn/types'
import { Tutor } from '@/store/tutors/types'
import { RootState } from '@/store/types';

export default Vue.extend({
  components: {
    StampHistories,
    ConfirmModal,
    SuccessModal,
    AddModal,
    EditModal,
    ErrorModal,
    Datepicker,
    Loading,
  },

  data: (): {
    deleteId: string
    date: Moment
    isLoading: boolean
  } => ({
    deleteId: '',
    date: moment(),
    isLoading: false,
  }),

  computed: {
    tutor(): Tutor {
      return this.$store.getters.findTutorById(this.$route.params.tutorId)
    },

    histories(): Stamp[] {
      return _.orderBy(this.$store.state.clockIn.stamps, 'date', 'desc')
    },
  },

  created() {
    this.date = moment(this.$route.params.date, 'YYYYMMDD')
    this.$store.dispatch('getTutor', { tutorId: this.$route.params.tutorId })
    this.fetch()
  },

  watch: {
    // ルートが変更されたらこのメソッドを再び呼び出します
    $route: 'fetch',
  },

  methods: {
    changeDateOnCalendar(date: Date) {
      this.date = moment(date)
      this.$router.push({
        name: 'ClockInDetail',
        params: { date: this.date.format('YYYYMMDD') },
      })
    },

    fetch(): void {
      if (this.isLoading) {
        return
      }

      this.isLoading = true
      this.$store.dispatch('fetchStamps', {
        id: this.$route.params.tutorId,
        date: this.date.format('YYYYMMDD'),
      })
        .finally(() => {
          this.isLoading = false
        })
    },

    addTime(): void {
      (this.$refs.addModal as any).open()
    },

    confirmDelete(attendanceId: string): void {
      this.deleteId = attendanceId;
      (this.$refs.confirmModal as ConfirmModal).open('Delete?')
    },

    editTime(stamp: Stamp): void {
      (this.$refs.editModal as any).open(stamp)
    },

    // 打刻情報の登録
    registerStamp(data: { time: string, type: clockInType }) {
      this.$store.dispatch('registerStamp', {
        userId: this.$route.params.tutorId,
        stamp: _.set(data, 'date', moment(this.date).format('YYYYMMDD')),
      })
        .then(() => {
          (this.$refs.addModal as any).close();
          (this.$refs.sucessModal as SuccessModal).open()
        })
        .catch((error) => {
          (this.$refs.addModal as any).close();
          (this.$refs.errorModal as ErrorModal).open(error.response.data.message)
        })
    },

    // 打刻情報の更新
    changeStamp(data: { attendanceId: string, time: string, type: clockInType }) {
      this.$store.dispatch(
        'changeStamp',
        {
          attendanceId: data.attendanceId,
          userId: this.$route.params.tutorId,
          stamp: _.set(data, 'date', moment(this.date).format('YYYYMMDD')),
        },
      )
        .then(() => {
          (this.$refs.editModal as any).close();
          (this.$refs.sucessModal as SuccessModal).open()
        })
        .catch((error) => {
          (this.$refs.editModal as any).close();
          (this.$refs.errorModal as ErrorModal).open(error.response.data.message)
        })
    },

    // 打刻情報の削除
    deleteStamp() {
      this.$store.dispatch(
        'deleteStamp',
        {
          attendanceId: this.deleteId,
          userId: this.$route.params.tutorId,
        },
      )
        .then(() => {
          (this.$refs.confirmModal as ConfirmModal).close();
          (this.$refs.sucessModal as SuccessModal).open()
        })
        .catch((error) => {
          (this.$refs.confirmModal as ConfirmModal).close();
          (this.$refs.errorModal as ErrorModal).open(error.response.data.message)
        })
    },
    hasAuth(acceptRoles: string[]): boolean {
      const auth = (this.$store.state as RootState).auth.profile?.authority
      return _.indexOf(acceptRoles, auth) !== -1
    },
  },
})
</script>

<style scoped>
.name-date-box {
  display:flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom:20px;
  padding:30px;
  background: #D8D8D8;
}
.tutor-info {
  font-size: 0.8em;
  padding: 5px 10px;
  border-radius: 20px;
  background: #2E75B5;
  color: #fff;
}

.tutor-name {
  font-size: 1.5em;
}

.datepickerInput {
  padding: 5px;
  border: 1px solid #AAAFB6;
  border-radius: 30px;
  width: 200px;
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

.lesson-date {
  background: #fff;
  border-radius:30px;
  border: #AAAFB6 1px solid;
}
</style>
